import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Loader2, MapPin, Calendar, Phone, Mail, RefreshCw, Send, AlertTriangle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { sendAllOrderNotifications } from "@/utils/orderNotifications";

// Define an interface for the order data we expect to display
interface OrderItemData { // For individual items
  name?: string; 
  title?: string; 
  quantity: number; 
  price: number; 
  main_image_url?: string; 
  product_name?: string; 
  price_at_purchase?: number; 
}

interface StoreProfileData { // For florist/store details
  id?: string;
  store_name?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  phone_number?: string;
  email?: string;
  logo_url?: string;
  // Add any other fields you expect from store_profiles
}

interface DisplayOrderData {
  id: string; 
  order_number?: string;
  total_amount?: number;
  buyer_email?: string; 
  customer_email?: string;
  is_delivery?: boolean;
  delivery_address?: string; 
  delivery_date?: string;
  delivery_time?: string;
  delivery_notes?: string; 
  special_instructions?: string;
  items?: OrderItemData[]; 
  status?: string;
  delivery_fee?: number;
  is_same_day?: boolean;
  pickup_location?: string; 
  store_profiles?: StoreProfileData | null; // Use the new interface
  florist_id?: any; // Kept for flexibility if store_profiles is sometimes null and florist_id is direct
  stripe_payment_intent_id?: string; 
  order_items?: any[]; // Raw data from Supabase if different from mapped 'items'
}

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clear } = useCart();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<DisplayOrderData | null>(null);
  const [florist, setFlorist] = useState<any>(null);
  const [notificationSent, setNotificationSent] = useState(false);
  const cartCleared = useRef(false);
  
  // Get Stripe session_id and our internal order_id from URL params
  const stripeSessionId = searchParams.get("session_id"); 
  const internalOrderIdFromUrl = searchParams.get("order_id");
  
  console.log('[CheckoutSuccess Mount] stripeSessionId from URL:', stripeSessionId);
  console.log('[CheckoutSuccess Mount] internalOrderIdFromUrl from URL:', internalOrderIdFromUrl);
  
  // Function to manually trigger notifications if needed
  const resendNotifications = async () => {
    if (!order?.id) return;
    
    toast({ title: "Sending notifications..." }); // Initial toast
    // DISABLED: The stripe-webhook-handler already sends payment confirmation emails
    // We don't want to send duplicate "order confirmed" emails
    /*
    const success = await sendAllOrderNotifications(order.id); // Corrected: Only pass orderId
      
      if (success) {
        toast({
          title: "Notifications sent",
          description: "Order confirmations have been sent successfully.",
        variant: "default", // Assuming 'default' is standard
        });
        setNotificationSent(true);
      } else {
      toast({
        title: "Notification error",
        description: "There was a problem sending order notifications. Please try again later.",
        variant: "destructive",
      });
    }
    */
    toast({
      title: "Notifications handled by payment system",
      description: "Payment confirmation emails are sent automatically.",
      variant: "default",
    });
  };
  
  // Test simple email service
  const testSimpleEmail = async () => {
    try {
      toast({
        title: "Sending test email",
        description: "Trying to send a test email directly...",
      });
      
      // Import the test utility dynamically to avoid import issues
      const { sendTestEmail } = await import('@/utils/testEmail');
      
      // Send to admin email by default, or customer email from order if available
      const testEmail = order?.customer_email || order?.buyer_email || "arthur23baris@gmail.com";
      
      // Use the test utility
      const result = await sendTestEmail(testEmail);
      
      if (result.success) {
        toast({
          title: "Test email sent",
          description: `Test email was sent successfully to ${testEmail}!`,
          variant: "default", // Changed from "success"
        });
        
        console.log("Email test result:", result);
      } else {
        toast({
          title: "Test email error",
          description: `Error: ${result.error}`,
          variant: "destructive",
        });
      }
    } catch (error: any) { // Typed error as any
      console.error("Error sending test email:", error);
      toast({
        title: "Test email error",
        description: `Error: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  const processStripePaymentConfirmation = useCallback(async () => {
    if (!stripeSessionId || !internalOrderIdFromUrl) {
      setError("Missing payment session or order information from Stripe redirect.");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      console.log(`[CheckoutSuccess] Processing Stripe confirmation. Session ID: ${stripeSessionId}, URL Order ID: ${internalOrderIdFromUrl}`);

      // 1. Get Checkout Session details from Stripe (includes PaymentIntent ID and status)
      const { data: sessionDetailsResponse, error: sessionDetailsError } = await supabase.functions.invoke(
        'get-stripe-checkout-session-details',
        { body: { session_id: stripeSessionId } }
      );

      if (sessionDetailsError || !sessionDetailsResponse || sessionDetailsResponse.error) {
        console.error("[CheckoutSuccess] Error fetching Stripe session details:", sessionDetailsError, sessionDetailsResponse?.error);
        throw new Error(sessionDetailsResponse?.error || sessionDetailsError?.message || "Failed to retrieve payment details from Stripe.");
      }
      
      console.log("[CheckoutSuccess] Stripe Session Details Response:", sessionDetailsResponse);
      const { paymentIntentId, paymentIntentStatus, metadata } = sessionDetailsResponse;
      
      console.log('[CheckoutSuccess process] Extracted metadata from Stripe session:', metadata);
      console.log('[CheckoutSuccess process] internalOrderIdFromUrl (available in this scope):', internalOrderIdFromUrl);

      const confirmedOrderId = metadata?.marketbloom_order_id || internalOrderIdFromUrl;
      console.log('[CheckoutSuccess process] confirmedOrderId determined as:', confirmedOrderId);

      if (!confirmedOrderId) {
        console.error("[CheckoutSuccess process] CRITICAL: confirmedOrderId is null or undefined. Metadata was:", metadata, "URL orderId was:", internalOrderIdFromUrl);
        throw new Error("Internal Order ID could not be determined from Stripe session or URL.");
      }

      if (!paymentIntentId) {
        throw new Error("Payment Intent ID not found in Stripe session details.");
        }
      
      // For manual capture, status should be 'requires_capture'
      // If 'succeeded', it means it was auto-captured (not our current setup)
      if (paymentIntentStatus !== 'requires_capture') {
        console.warn(`[CheckoutSuccess] PaymentIntent status is ${paymentIntentStatus}, not 'requires_capture'. Check Stripe dashboard and payment flow configuration.`);
        // Depending on your business logic, you might still proceed or show a specific message.
        // For now, we'll proceed assuming the main thing is to link the PI to the order.
      }

      // 2. Update your internal order with the PaymentIntent ID and set status
      const newOrderStatus = "awaiting_florist_confirmation"; // Fixed: use lowercase with underscores to match DB constraint
      console.log(`[CheckoutSuccess] Updating order ${confirmedOrderId} with PI: ${paymentIntentId}, Status: ${newOrderStatus}`);
      
      const { data: updateOrderResponse, error: updateOrderError } = await supabase.functions.invoke(
        'update-order-after-stripe-auth',
        {
          body: {
            order_id: confirmedOrderId,
            payment_intent_id: paymentIntentId,
            new_order_status: newOrderStatus,
          }
        }
      );

      if (updateOrderError || !updateOrderResponse?.success) {
        console.error("[CheckoutSuccess] Error updating order with payment details:", updateOrderError, updateOrderResponse?.error);
        // Even if this fails, the payment was authorized. Critical to flag this for manual reconciliation.
        setError("Payment authorized, but failed to update order details in our system. Please contact support with your Order ID.");
        // Do not clear cart or navigate away if order update fails, so user sees the error and order ID.
        setLoading(false);
        return; // Stop further processing like notifications if order update failed
      }
      console.log("[CheckoutSuccess] Order updated successfully with Stripe details.");

      // 3. Now fetch the fully updated order for display
      const { data: finalOrderDataResult, error: finalOrderError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          total_amount,
          buyer_email,
          customer_email,
          is_delivery,
          delivery_address,
          delivery_date,
          delivery_time,
          delivery_notes,
          special_instructions,
          status,
          delivery_fee,
          is_same_day,
          pickup_location,
          stripe_payment_intent_id,
          store_id, 
          store_profiles (*),
          order_items (*)
        `)
        .eq('id', confirmedOrderId)
        .single();

      if (finalOrderError || !finalOrderDataResult) {
        console.error('[CheckoutSuccess] Error fetching final order details:', finalOrderError);
        throw new Error("Order details could not be loaded after payment confirmation.");
      }
      
      const typedFinalOrderData = finalOrderDataResult as unknown as DisplayOrderData;
      
      // More robust mapping for order_items
      if (finalOrderDataResult && 
          typeof finalOrderDataResult === 'object' && 
          'order_items' in finalOrderDataResult && 
          Array.isArray(finalOrderDataResult.order_items)) {
        typedFinalOrderData.items = finalOrderDataResult.order_items.map((oi: any) => ({
          name: oi.product_name || oi.name || 'Unknown Item',
          quantity: oi.quantity || 0,
          price: oi.price_at_purchase || oi.price || 0,
          main_image_url: oi.main_image_url,
          title: oi.title, 
        }));
      } else if (!typedFinalOrderData.items) { // Ensure items is initialized if not set by mapping
        typedFinalOrderData.items = [];
      }

      // More robust assignment for store_profiles
      if (finalOrderDataResult && 
          typeof finalOrderDataResult === 'object' && 
          'store_profiles' in finalOrderDataResult && 
          finalOrderDataResult.store_profiles) {
        typedFinalOrderData.store_profiles = finalOrderDataResult.store_profiles as StoreProfileData;
      } else {
        typedFinalOrderData.store_profiles = null;
      }

      setOrder(typedFinalOrderData);
      // Use the explicitly typed store_profiles from the order data
      setFlorist(typedFinalOrderData.store_profiles);
      setError(null);

      // 4. Send notifications
      if (!notificationSent) {
        console.log("[CheckoutSuccess] Sending order notifications...");
        // DISABLED: The stripe-webhook-handler already sends payment confirmation emails
        // We don't want to send duplicate "order confirmed" emails here
        /*
        sendAllOrderNotifications(confirmedOrderId) // Corrected: Only pass orderId
          .then(success => {
            if(success) console.log("Notifications sent successfully after Stripe confirmation.");
            else console.warn("Failed to send notifications after Stripe confirmation.");
            setNotificationSent(success);
          })
          .catch(err => console.error("Error sending notifications post-Stripe:", err));
        */
        console.log("[CheckoutSuccess] Skipping duplicate notifications - webhook handles this");
        setNotificationSent(true);
      }

      // 5. Clear the cart (only once)
      if (!cartCleared.current) {
        clear();
        cartCleared.current = true;
        console.log("[CheckoutSuccess] Cart cleared.");
      }

    } catch (err: any) { // More specific error typing is hard without knowing all possible errors
      const error = err as any; // Cast to any to access message/type properties
      console.error("[CheckoutSuccess] Error in processStripePaymentConfirmation:", error);
      setError(error.message || "An error occurred while confirming your payment. Please contact support.");
      // Potentially display the internalOrderIdFromUrl if available for support reference
    } finally {
      setLoading(false);
    }
  }, [stripeSessionId, internalOrderIdFromUrl, toast, clear, notificationSent, navigate]);
  
  useEffect(() => {
    if (stripeSessionId) { // Prioritize processing if Stripe session_id is present
      processStripePaymentConfirmation();
    } else if (internalOrderIdFromUrl) {
      // This case handles if user navigates directly to /order-confirmation?order_id=... 
      // (e.g. from email link) without a Stripe session_id.
      // We should just try to load and display the order.
      console.log(`[CheckoutSuccess] No Stripe session_id, attempting to load order ${internalOrderIdFromUrl} directly.`);
      setLoading(true);
      supabase.from('orders')
        .select(`*, store_profiles(*), order_items(*)`)
        .eq('id', internalOrderIdFromUrl as any) // Cast to any for .eq()
        .single()
        .then(({ data: orderDataResult, error: orderFetchError }) => {
          if (orderFetchError || !orderDataResult) {
            setError("Could not load order details.");
            console.error("Error loading order directly:", orderFetchError);
          } else {
            const typedData = orderDataResult as unknown as DisplayOrderData;
            // More robust mapping for order_items from direct fetch
            if (orderDataResult && 
                typeof orderDataResult === 'object' && 
                'order_items' in orderDataResult && 
                Array.isArray(orderDataResult.order_items) && 
                (!typedData.items || typedData.items.length === 0)) {
                typedData.items = orderDataResult.order_items.map((oi: any) => ({
                  name: oi.product_name || oi.name || 'Unknown Item',
                  quantity: oi.quantity || 0,
                  price: oi.price_at_purchase || oi.price || 0,
                  main_image_url: oi.main_image_url,
                  title: oi.title,
                }));
            } else if (!typedData.items) {
                typedData.items = [];
            }
            // More robust assignment for store_profiles from direct fetch
            if (orderDataResult && 
                typeof orderDataResult === 'object' && 
                'store_profiles' in orderDataResult && 
                orderDataResult.store_profiles) {
              typedData.store_profiles = orderDataResult.store_profiles as StoreProfileData;
            } else {
              typedData.store_profiles = null;
            }

            setOrder(typedData);
            setFlorist(typedData.store_profiles); // Use the explicitly typed store_profiles
          }
          setLoading(false);
        });
    } else {
      setError("Order information is missing from the URL. No session_id or order_id found.");
      setLoading(false);
    }
  }, [stripeSessionId, internalOrderIdFromUrl, processStripePaymentConfirmation]);
  
  // Function to format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString('en-AU', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-red-500 pt-24 pb-12">
        <main className="container mx-auto px-4 py-8">
          <h1 className="text-white text-4xl font-bold text-center mb-4">STYLING TEST ACTIVE</h1>
          <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
            <CardHeader className="text-center bg-[#967F81] text-white p-6">
              <CheckCircle className="h-16 w-16 mx-auto mb-3" />
              <CardTitle className="text-3xl font-semibold">
                Order Confirmed! (Styling Test)
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-6 space-y-6">
              <div className="text-center py-10">
                <Loader2 className="h-10 w-10 animate-spin text-[#967F81] mx-auto mb-3" />
                <p className="text-md text-gray-700">Confirming Your Order (Styling Test)...</p>
                <p className="text-sm text-gray-500">Please wait, we're finalizing your order details...</p>
              </div>
              {error && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md">
                  <div className="flex items-center mb-1">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    <p className="font-medium">There was a problem (Styling Test)</p>
                  </div>
                  <p className="text-sm">{error}</p>
                  <p className="text-xs mt-2">Debug Info - URL Params: session_id = {searchParams.get("session_id")}, order_id = {searchParams.get("order_id")}</p>
                </div>
              )}
              {!loading && !error && !order && (
                <div className="text-center py-10">
                  <p className="text-lg text-gray-600">No order data to display yet. (Styling Test)</p>
                </div>
              )}
              {!loading && !error && order && (
                <>
                  <div className="text-center">
                    <p className="text-lg text-gray-700 mb-1">Thank you for your order, {order.buyer_email || 'Valued Customer'}! (Styling Test)</p>
                    <p className="text-sm text-gray-500 mb-4">Your order status is: {order.status}</p>
                    <pre className="text-xs text-left bg-gray-100 p-2 overflow-auto">{JSON.stringify(order, null, 2)}</pre>
                  </div>
                </>
              )}
            </CardContent>
          </div>
        </main>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] pt-20 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-2xl font-semibold text-red-700">Order Confirmation Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-700 mb-4">{error}</p>
            <Button onClick={() => navigate("/")} className="flex items-center">
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] pt-20 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-semibold mb-2 text-brand-secondary">
              Order Not Found
            </h1>
            <p className="text-brand-secondary/70 mb-4">
              We couldn't find your order details. Please check your information or contact support.
            </p>
            <Button onClick={() => navigate("/")} className="flex items-center">
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FAF7F0] pt-20">
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <Card className="mb-8">
            <CardHeader className="text-center pb-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-3xl font-semibold text-brand-secondary">
                Thank You For Your Order!
              </CardTitle>
              <CardDescription className="text-lg">
                Your order is now awaiting confirmation from {florist?.store_name || "the florist"}.
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-0">
              <div className="space-y-6">
                <div className="bg-[#F8F7F4] rounded-lg p-4 text-center">
                  <p className="text-sm text-brand-secondary/70 mb-1">Your Order Number</p>
                  <p className="text-xl font-semibold text-brand-secondary">
                    {order?.order_number || internalOrderIdFromUrl}
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                  <h3 className="font-semibold text-lg text-blue-800">What Happens Next?</h3>
                  <ul className="list-decimal list-inside space-y-2 text-blue-700">
                    <li>
                      <span className="font-medium">Payment Authorized:</span> Your payment has been successfully authorized.
                    </li>
                    <li>
                      <span className="font-medium">Awaiting Florist Confirmation:</span> {florist?.store_name || "The florist"} will review your order.
                    </li>
                    <li>
                      <span className="font-medium">Payment Capture & Preparation:</span> Once confirmed, the payment will be captured, and {florist?.store_name} will begin preparing your order for {order?.is_delivery ? 'delivery' : 'pickup'} on {formatDate(order?.delivery_date)}.
                    </li>
                    <li>
                      <span className="font-medium">Updates:</span> You'll receive email and SMS updates on your order status.
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-brand-secondary">Order Details</h3>
                  {order?.items && order.items.length > 0 ? (
                    <div className="space-y-4 bg-[#F8F7F4] rounded-lg p-4">
                      {order.items.map((item: any, index: number) => (
                        <div key={index} className="flex items-center gap-4 border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                          {item.main_image_url ? (
                            <div className="h-16 w-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                              <img src={item.main_image_url} alt={item.name || item.title} className="h-full w-full object-cover" />
                            </div>
                          ) : (
                            <div className="h-16 w-16 rounded-md bg-gray-200 flex-shrink-0"></div> 
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-brand-secondary">{item.name || item.title || 'Product'}</h4>
                            <p className="text-sm text-brand-secondary/70">
                              Qty: {item.quantity} × ${parseFloat(String(item.price)).toFixed(2)} 
                            </p>
                          </div>
                          <p className="font-medium text-brand-secondary">
                            ${(parseFloat(String(item.price)) * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No item details available.</p>
                  )}
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-brand-secondary">
                      {order?.is_delivery ? "Delivery Information" : "Pickup Information"}
                    </h3>
                    <div className="bg-[#F8F7F4] rounded-lg p-4 space-y-2 text-sm text-brand-secondary/80">
                      {order?.is_delivery ? (
                        order.delivery_address ? (() => {
                          try {
                            const addressObj = JSON.parse(order.delivery_address);
                            const formattedAddress = [
                              addressObj.street,
                              addressObj.suburb,
                              addressObj.state,
                              addressObj.postcode,
                            ].filter(Boolean).join(', ');
                            return (
                              <p className="flex items-start">
                                <MapPin className="h-4 w-4 mr-2 text-brand-primary mt-0.5 flex-shrink-0" />
                                {formattedAddress}
                              </p>
                            );
                          } catch (e) {
                            console.warn("Could not parse delivery_address JSON:", order.delivery_address);
                            return (
                              <p className="flex items-start">
                          <MapPin className="h-4 w-4 mr-2 text-brand-primary mt-0.5 flex-shrink-0" />
                                {order.delivery_address} 
                              </p>
                            );
                          }
                        })() : (
                          <p>No delivery address provided.</p>
                        )
                      ) : (
                        <p>{order?.pickup_location || 'No pickup location provided.'}</p>
                      )}
                      {order?.delivery_date && (
                        <p className="flex items-start">
                          <Calendar className="h-4 w-4 mr-2 text-brand-primary" />
                          Scheduled: {formatDate(order.delivery_date)}{order.delivery_time ? ` at ${order.delivery_time}` : ''}
                        </p>
                      )}
                      {typeof order?.delivery_fee === 'number' && (
                        <p className="text-sm text-brand-secondary/80">
                          <span className="font-medium">Delivery Fee:</span> ${parseFloat(String(order.delivery_fee)).toFixed(2)}
                        </p>
                      )}
                      {order?.is_same_day && (
                        <p className="font-medium text-brand-primary">
                          Same-Day {order?.is_delivery ? "Delivery" : "Pickup"}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {florist && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3 text-brand-secondary">Need Help?</h3>
                      <div className="bg-[#F8F7F4] rounded-lg p-4 space-y-3">
                        {florist.logo_url && (
                          <div className="mb-4 flex justify-center">
                            <img src={florist.logo_url} alt={`${florist.store_name} Logo`} className="h-20 w-auto rounded-md object-contain" />
                          </div>
                        )}
                        <p className="text-sm font-medium text-center">
                          Contact {florist.store_name} directly:
                        </p>
                        {florist.email && (
                          <a href={`mailto:${florist.email}`} className="flex items-center text-sm hover:text-brand-primary transition-colors">
                            <Mail className="h-4 w-4 mr-2 text-brand-primary" />
                            {florist.email}
                          </a>
                        )}
                        {florist.phone && (
                          <a href={`tel:${florist.phone}`} className="flex items-center text-sm hover:text-brand-primary transition-colors">
                            <Phone className="h-4 w-4 mr-2 text-brand-primary" />
                            {florist.phone}
                          </a>
                        )}
                        <p className="text-xs text-brand-secondary/70 mt-2">
                          Please have your order number ready when contacting the florist.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {order?.delivery_notes && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-brand-secondary">Special Instructions</h3>
                    <div className="bg-[#F8F7F4] rounded-lg p-4 text-sm italic">
                      "{order.delivery_notes}"
                    </div>
                  </div>
                )}
                
                <Separator />
                
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-brand-secondary">Order Status & Payment</h3>
                  <div className="bg-[#F8F7F4] rounded-lg p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-brand-secondary/70">Order Status</span>
                      <span className="text-sm font-medium text-blue-600">
                        {order?.status || "Pending"} 
                      </span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-brand-secondary/70">Payment Status</span>
                      <span className="text-sm font-medium text-blue-600">
                         Authorized {/* Since it's manual capture */}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-brand-secondary/70">Total Amount</span>
                      <span className="text-sm font-medium">
                        ${parseFloat(String(order?.total_amount || "0")).toFixed(2)}
                      </span>
                    </div>
                    {order?.stripe_payment_intent_id && (
                       <div className="flex justify-between mt-1 text-xs">
                         <span className="text-gray-500">Payment ID:</span>
                         <span className="text-gray-500 truncate max-w-[150px]">{order.stripe_payment_intent_id}</span>
                       </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row gap-4 mt-6">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => navigate("/")}
              >
                Continue Shopping
              </Button>
                <Button
                  className="w-full sm:w-auto bg-[#967F81] hover:bg-[#843329] text-white"
                  onClick={() => navigate("/customer-dashboard")}
                >
                  View My Orders
                </Button>
            </CardFooter>
          </Card>
          
          <div className="text-center space-y-2 text-sm text-brand-secondary/60 mt-6">
            <p>We've sent a confirmation email with your order details to {order?.buyer_email || order?.customer_email}.</p>
            <p>You'll receive SMS updates about your order status and {order?.is_delivery ? 'delivery' : 'pickup'} timing.</p>
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs flex items-center gap-1"
                onClick={resendNotifications}
              >
                <RefreshCw className="h-3 w-3" /> 
                Resend Confirmation Email
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutSuccess;
